import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { products } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";

export default function BestSellersSection() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [wishlist, setWishlist] = useState<number[]>([]);
  const [addedIds, setAddedIds] = useState<number[]>([]);
  const { addToCart } = useCart();

  const featured = products.slice(0, 9);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleAddToCart = (product: typeof products[0]) => {
    addToCart({
      id: product.id,
      name: product.name,
      nameF: product.nameF,
      price: product.price,
      image: product.image,
    });
    setAddedIds((prev) => [...prev, product.id]);
    setTimeout(() => {
      setAddedIds((prev) => prev.filter((id) => id !== product.id));
      // Redirect to Facebook Marketplace after adding to cart
      window.open("https://www.facebook.com/marketplace/", "_blank");
    }, 800);
  };

  const toggleWishlist = (id: number) => {
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const badgeColors: Record<string, string> = {
    popular: "bg-rose-500",
    new: "bg-emerald-500",
    sale: "bg-amber-500",
  };

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-[#f5ede0]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)" }}
        >
          <div>
            <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
              — Most Loved —
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold text-stone-800"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("best_title")}
            </h2>
            <p className="text-gray-600 text-lg mt-2">{t("best_subtitle")}</p>
          </div>
          <a
            href="/shop"
            className="flex items-center gap-2 text-amber-700 font-semibold text-sm hover:gap-3 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            {t("common_view_all")}
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-product-shop>
          {featured.map((product, i) => (
            <div
              key={product.id}
              className="group bg-white rounded-2xl overflow-hidden border border-rose-100 hover:border-rose-200 transition-all duration-500 hover:-translate-y-2"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(50px)",
                transition: `all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)`,
                transitionDelay: `${i * 0.1}s`,
              }}
            >
              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={product.image}
                  alt={isFr ? product.nameF : product.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                {/* Badge */}
                {product.badge && (
                  <span className={`absolute top-3 left-3 px-2.5 py-1 ${badgeColors[product.badge]} text-white text-xs font-bold rounded-full uppercase`}>
                    {t(`common_${product.badge}`)}
                  </span>
                )}
                {/* Wishlist */}
                <button
                  onClick={() => toggleWishlist(product.id)}
                  className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 cursor-pointer shadow-sm"
                >
                  <i className={`${wishlist.includes(product.id) ? "ri-heart-fill text-amber-600" : "ri-heart-line text-gray-400"} text-base`}></i>
                </button>

              </div>

              {/* Info */}
              <div className="p-5">
                <h3
                  className="font-bold text-gray-800 text-base mb-1 group-hover:text-amber-700 transition-colors duration-200"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {isFr ? product.nameF : product.name}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">
                  {isFr ? product.descriptionF : product.description}
                </p>

                {/* Rating */}
                <div className="flex items-center gap-1.5 mb-4">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className={`ri-star-fill text-xs ${j < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>
                    ))}
                  </div>
                  <span className="text-xs text-gray-500">({product.reviews})</span>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end">
                  <div className="flex items-center gap-2">
                    <a
                      href={`https://wa.me/237678012159?text=Hello! I'd like to order: ${isFr ? product.nameF : product.name} - ${product.price.toLocaleString()} XAF`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-500 hover:text-white transition-all duration-200 cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-base"></i>
                    </a>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        addedIds.includes(product.id)
                          ? "bg-green-500 text-white scale-95"
                          : "bg-gradient-to-r from-amber-600 to-rose-600 text-white hover:shadow-lg hover:scale-105"
                      }`}
                    >
                      <i className={`${addedIds.includes(product.id) ? "ri-checkbox-circle-line" : "ri-shopping-bag-line"} text-sm`}></i>
                      {addedIds.includes(product.id) ? "Added!" : t("best_add")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}